import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ListPaginationBar } from '../ui/ListPaginationBar';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Company } from '../../services/coreCompaniesApi';
import {
  ServiceTicketSummary,
  listServiceTicketHistory,
} from '../../services/customerSuccessTicketsApi';
import { getTicketStageConfig } from '../../types/TicketStage';

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

const HISTORY_PAGE_SIZE = 25;

type TicketHistoryPanelProps = {
  companies: Company[];
  onSelect: (ticket: ServiceTicketSummary) => void;
  selectedId?: string | null;
};

export function TicketHistoryPanel({ companies, onSelect, selectedId }: TicketHistoryPanelProps) {
  const [items, setItems] = useState<ServiceTicketSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');

  const loadHistory = useCallback(async (targetPage = page): Promise<void> => {
    setLoading(true);
    try {
      const result = await listServiceTicketHistory({
        search: search || undefined,
        companyId: companyId || undefined,
        updatedFromUtc: updatedFrom ? `${updatedFrom}T00:00:00.000Z` : undefined,
        updatedToUtc: updatedTo ? `${updatedTo}T23:59:59.999Z` : undefined,
        page: targetPage,
        pageSize: HISTORY_PAGE_SIZE,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar histórico de chamados.');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, search, updatedFrom, updatedTo]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchDraft.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    setPage(1);
  }, [search, companyId, updatedFrom, updatedTo]);

  useEffect(() => {
    void loadHistory(page);
  }, [loadHistory, page]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className="erp-label md:col-span-2">
          Buscar
          <input
            className="erp-input"
            placeholder="Código, título, empresa, responsável..."
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <label className="erp-label">
          Empresa
          <select className="erp-input" value={companyId} onChange={(event) => setCompanyId(event.target.value)}>
            <option value="">Todas</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label">
          Atualizado de
          <input
            type="date"
            className="erp-input"
            value={updatedFrom}
            onChange={(event) => setUpdatedFrom(event.target.value)}
          />
        </label>
        <label className="erp-label">
          Atualizado até
          <input
            type="date"
            className="erp-input"
            value={updatedTo}
            onChange={(event) => setUpdatedTo(event.target.value)}
          />
        </label>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando histórico..." />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-600">Nenhum chamado arquivado encontrado.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
          <table className="erp-table min-w-full">
            <thead>
              <tr>
                <th>Código</th>
                <th>Título</th>
                <th>Empresa</th>
                <th>Status</th>
                <th>Prioridade</th>
                <th>Responsável</th>
                <th>Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ticket) => (
                <tr
                  key={ticket.id}
                  className={selectedId === ticket.id ? 'bg-primary-50' : 'cursor-pointer hover:bg-secondary-50'}
                  onClick={() => onSelect(ticket)}
                >
                  <td className="font-mono text-xs">{ticket.ticketCode}</td>
                  <td className="max-w-xs truncate">{ticket.title}</td>
                  <td>{ticket.companyName}</td>
                  <td>{getTicketStageConfig(ticket.status).label}</td>
                  <td>{PRIORITY_LABELS[ticket.priority] ?? ticket.priority}</td>
                  <td>{ticket.assignedToUserName ?? '—'}</td>
                  <td>{new Date(ticket.updatedAtUtc).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={HISTORY_PAGE_SIZE}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => current + 1)}
      />
    </div>
  );
}
